import React from 'react'
import { RouteComponentProps } from 'react-router'

import { Helmet } from 'react-helmet'
import { Footer, Grid } from '@mytours/components'
import classNames from 'classnames'

import {
	CollectionListContainer as Container,
	CollectionItem,
	COLLECTION_ITEM_TYPE_COLLECTION,
	COLLECTION_ITEM_SUBTYPE_LIST,
	Collection,
	isCollectionBundleLocked,
	offeredBundlesFor,
} from 'mytours-read-common'
import { Props as HeaderProps } from '../../../common/components/header'

import { CollectionListNavigationProps } from '../../types'
import { layoutMode } from '../../functions'

import Page from '../../../common/components/Page'
import Modal from '../../../common/components/modal'

import {
	GetCollectionItemsIndexResponseData,
	GetCollectionsShowResponse,
	RedeemCode,
	MapRoute,
} from 'mytours-read-api-typescript'

import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { validateCollectionCode } from 'mobile_web/modules/lockapp/functions'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'

import MapLayout from 'mobile_web/modules/map/parts/MapActionsLayout'
import { loadingTimeout } from 'mobile_web/modules/common/functions'
import DefaultPagePlaceholder from 'mobile_web/modules/common/components/placeholders/DefaultPage'
import Page404 from 'mobile_web/404'
import Content from 'mobile_web/modules/common/components/content'

import '../styles/list.less'
import CollectionListContent from './CollectionListContent'
import { CollectionItemsSlug } from 'mobile_web/modules/navigation/routes'
import { addQueryParam, removeQueryParam } from 'mobile_web/modules/navigation/queryParams'
import ErrorMessageHandler from 'mobile_web/modules/common/components/error-message-handler'
import LockAppComponent from 'mobile_web/modules/lockapp/components/LockApp'
import BundleUnlockButton from 'mobile_web/modules/lockapp/components/BundleUnlockButton'
import BundleOnlyLock from 'mobile_web/modules/lockapp/components/BundleOnlyLock'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'

interface Props
	extends
		RouteComponentProps<CollectionListRouteParams>,
		Container.Props,
		Container.Actions,
		CollectionListNavigationProps {
	isPageContent: boolean
	isInsideModal?: boolean
}

interface PageItem {
	subtype: string
	id: number
	version: number
}

interface State {
	showMap: boolean
	mapModalDidPresent: boolean
	showPage: boolean
	pageModalDidPresent: boolean
	page?: PageItem
	codeLock: boolean
	hasWebPrice: boolean
	loading: boolean
	showPageScreenModal: boolean
	currentMapItem: CollectionItem
}

interface CollectionListRouteParams {
	id: string
	version: string
}

class CollectionList extends React.Component<Props, State> {
	watchLocationRunned = false
	opennedMapModalByHash = false

	get collectionType(): 'list' | 'menu' {
		const collectionType =
			this.props?.collection?.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionMenu
				? GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu
				: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List

		return collectionType
	}

	unListenHistory: () => void

	constructor(props: Props) {
		super(props)

		this.state = {
			showMap: false,
			mapModalDidPresent: false,
			showPage: false,
			pageModalDidPresent: false,
			page: undefined,
			codeLock: null,
			hasWebPrice: null,
			loading: props.loading,
			showPageScreenModal: false,
			currentMapItem: null,
		}
	}

	componentDidMount() {
		const { collectionId, collectionVersion, accountId, language, loadCollectionDetails, loadCollectionItems } =
			this.props

		if (collectionVersion) {
			loadCollectionDetails(collectionId, collectionVersion, language, accountId)
			loadCollectionItems(collectionId, collectionVersion, language, accountId)
		}

		this.historyListener()
	}

	componentDidUpdate(prevProps: Container.Props) {
		const {
			collection,
			validateCode,
			collectionId,
			collectionVersion,
			accountId,
			loading,
			language,
			loadCollectionDetails,
			loadCollectionItems,
			unlockedViaBundle,
		} = this.props

		if (
			prevProps.collectionId !== collectionId ||
			language !== prevProps.language ||
			prevProps.collectionVersion !== collectionVersion ||
			(prevProps.collection && !collection)
		) {
			if (collectionVersion) {
				loadCollectionDetails(collectionId, collectionVersion, language, accountId)
				loadCollectionItems(collectionId, collectionVersion, language, accountId)
			}

			// clear lock code state when collection changed
			this.setState({ codeLock: null, hasWebPrice: null })

			return collectionId
		}

		this.openMapModalByHash()

		if (
			(this.state.codeLock === null && collection?.hasOwnProperty('code_lock')) ||
			(this.state.hasWebPrice === null && collection?.pricing?.web_price?.amount)
		) {
			// A collection inside a redeemed bundle is unlocked regardless of its own code/price gate.
			const codeLock = collection.code_lock && !unlockedViaBundle
			const hasWebPrice = !!collection.pricing?.active && !!collection.pricing?.web_price?.amount && !unlockedViaBundle
			this.setState({ codeLock, hasWebPrice }, async () => {
				if (codeLock || hasWebPrice) await validateCollectionCode(collection, validateCode)
			})
		}

		this.checkUnlockApp(prevProps)

		// If a bundle redemption unlocks this collection after its own lock was already applied,
		// clear the gate so the list becomes accessible without a collection code.
		if (unlockedViaBundle && (this.state.codeLock || this.state.hasWebPrice)) {
			this.setState({ codeLock: false, hasWebPrice: false })
		}

		if (prevProps.loading !== loading) {
			// loading with timeout to change skeleton with images content smoothly
			loadingTimeout(loading, val => this.setState({ loading: val }))
		}
	}

	componentWillUnmount(): void {
		this.unListenHistory?.()
		if (this.state.showMap) {
			this.toggleMapModal(false)
		}
	}

	private historyListener = () => {
		let prevPath = ionHistory.location.pathname
		let prevHash = ionHistory.location.hash
		this.unListenHistory = ionHistory.listen(({ pathname, hash }) => {
			// fix map view on back action from list page
			if (prevPath !== pathname && prevPath?.includes(pathname)) {
				setTimeout(() => {
					this.forceUpdate()
				}, 200)
			}

			if (
				(prevPath !== pathname && !prevPath?.includes(pathname)) || // Close map modal when route changed
				(prevHash === '#list-map' && hash === '') // Close map modal when press back button
			) {
				this.toggleMapModal(false)
			}

			prevPath = pathname
			prevHash = hash
		})
	}

	private checkUnlockApp = (prevProps: Container.Props) => {
		const { lockCollection, collection, validateCode } = this.props
		if (!lockCollection.loading && lockCollection.loaded) {
			if (lockCollection.status === 'valid' && !prevProps.lockCollection.status) {
				this.setState({ codeLock: false, hasWebPrice: false })
			} else if (
				(lockCollection.status === 'invalid' || lockCollection.status === 'expired') &&
				!prevProps.lockCollection.status
			) {
				validateCollectionCode(collection, validateCode)
			}
		}
	}

	private openMapModalByHash = () => {
		const { loading, error, collection = {} as Collection } = this.props
		const { map, map_view_enabled: mapViewEnabled } = collection
		const { showMap } = this.state

		const mappableList = !!(mapViewEnabled && map)
		if (
			!this.opennedMapModalByHash &&
			ionHistory.location.hash === '#list-map' &&
			!loading &&
			!error &&
			!showMap &&
			mappableList &&
			!this.showMapLayout
		) {
			this.opennedMapModalByHash = true
			setTimeout(() => {
				this.toggleMapModal(true)
			}, 0)
		}
	}

	get listItems(): CollectionItem[] {
		const { items, collectionId } = this.props
		//deny collection list item to be inside itself
		const listItems = items?.filter(
			item =>
				item.item_id !== collectionId ||
				item.item_type !== COLLECTION_ITEM_TYPE_COLLECTION ||
				item.item_subtype !== COLLECTION_ITEM_SUBTYPE_LIST,
		)

		return listItems
	}

	onItemClicked = (item: CollectionItem, openInModal?: boolean) => (): void => {
		if (this.state.loading) return

		if (!openInModal) {
			this.navigateToItem(item)

			if (this.props.onBackAction) {
				this.props.onBackAction({ closeMapModal: true })
			}
		} else {
			addQueryParam('show-detail-modal', true)
			addQueryParam('collection-item-id', item.id)
		}
	}

	navigateToItem = (item: CollectionItem) => {
		const { collectionId, collectionVersion, collectionTitle, accountId } = this.props
		const { showMap } = this.state

		const params = {
			collectionId,
			collectionVersion,
			collectionAccountId: accountId,
			accountId: item.account_id,
			itemId: item.item_id,
			collectionTitle,
			title: collectionTitle,
		}

		const queryParams = {}

		if (this.showMapLayout || showMap) {
			queryParams['show-detail-modal'] = 'true'
			queryParams['collection-item-id'] = item.item_id
		}

		NavigationService.navigateToCollectionItems({
			params,
			collectionType: this.collectionType as CollectionItemsSlug,
			itemType: item.item_subtype,
			queryParams,
		})
	}

	renderMap = (): JSX.Element => {
		const { showMap } = this.state

		return (
			<Modal
				isOpen={showMap}
				cssClass="map-modal"
				onDidPresent={() => this.toggleMapModalDidPresent(true)}
				onDidDismiss={() => {
					this.toggleMapModalDidPresent(false)
					this.toggleMapModal(false)
				}}
			>
				<Content>
					<Grid className="map-modal-content">{this.renderMapContent({ showMapLayout: false })}</Grid>
				</Content>
				<Footer>
					<AudioPlayerBarComponent className="full-width" bottomOffset={showMap ? 0 : -1} />
				</Footer>
			</Modal>
		)
	}

	renderMapContent = ({ showMapLayout }: { showMapLayout: boolean }): JSX.Element => {
		const { collection, accountId } = this.props
		const hasPrevPath = (ionHistory.location?.state as any)?.prevPagePath

		return (
			<MapLayout
				accountId={accountId}
				details={collection}
				itemType={MapRoute.LinkedTypeEnum.Collection}
				items={this.listItems}
				currentItemIndex={-1}
				boundAll={true}
				onExit={!showMapLayout ? () => this.toggleMapModal(false) : null}
				onCloseMap={!showMapLayout ? () => this.toggleMapModal(false) : null}
				onGoBack={hasPrevPath ? ionHistory.goBack : null}
				onItemNavigate={this.onMapItemNavigate}
				isMapLayout={showMapLayout}
				onSelectItemFromMenu={(id: number) => {
					addQueryParam('show-detail-modal', true)
					addQueryParam('collection-item-id', id)
				}}
			/>
		)
	}

	toggleMapModalDidPresent = (status: boolean) => {
		this.setState({ mapModalDidPresent: status })
		if (!status) {
			ionHistory.replace(ionHistory.location.pathname)
		} else {
			if (!this.showMapLayout) {
				window.location.hash = '#list-map'
			}
		}
	}

	togglePageModalDidPresent = (status: boolean) => {
		this.setState({ pageModalDidPresent: status })
	}

	toggleMapModal = (showMap?: boolean) => {
		removeQueryParam('show-detail-modal')
		removeQueryParam('collection-item-id')

		this.setState({ showMap })
	}

	togglePageModal = (showPage?: boolean, collectionItem?: CollectionItem) => {
		if (showPage && collectionItem) {
			this.setState({
				showPage,
				page: {
					id: collectionItem.item_id,
					version: collectionItem.version,
					subtype: collectionItem.item_subtype,
				},
			})
		} else {
			this.setState({
				showPage,
				page: undefined,
			})
		}
	}

	get showMapLayout(): boolean {
		const { collection = {} as Collection, isInsideModal } = this.props
		const { initial_layout: initialLayout } = collection

		return layoutMode(initialLayout) === 'map' && !isInsideModal
	}

	onMapItemNavigate = (item: CollectionItem): void => {
		this.onItemClicked(item, true)()
	}

	render() {
		const {
			error,
			collection = {} as Collection,
			isPageContent,
			onBackAction,
			lockCollection,
			isInsideModal,
		} = this.props
		const { showMap, codeLock, hasWebPrice, loading } = this.state

		const {
			map,
			title,
			description,
			cover_image: coverImage,
			logo,
			logo_placement: logoPlacement,
			header_layout: headerLayout,
			map_view_enabled: mapViewEnabled,
			code_title: codeTitle,
			code_text: codeText,
			code_background_media_item: codeBackgroundMediaItem,
		} = collection

		const coverImageUrl = coverImage?.file
			? generateImageUrl(coverImage.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: undefined
		const coverImageSrcSet = coverImage?.file
			? generateImageSrcSet(coverImage.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: undefined

		const logoImageUrl = logo?.file
			? generateImageUrl(logo.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: undefined
		const logoImageSrcSet = logo?.file
			? generateImageSrcSet(logo.file, {
					imageSizePreset: ImageSizePreset.Row,
				})
			: undefined

		const mappableList = !!(mapViewEnabled && map)

		const ownLock = codeLock || hasWebPrice
		const bundleLocked = isCollectionBundleLocked(collection, this.props.unlockedViaBundle, lockCollection)
		const unlockingBundles = ownLock || bundleLocked ? offeredBundlesFor(collection) : []

		if (!ownLock && bundleLocked) {
			return <BundleOnlyLock bundles={unlockingBundles} accountId={collection.account_id} />
		}

		if (ownLock && !lockCollection.loading) {
			return (
				<LockAppComponent
					linkedId={collection.id}
					linkedType={RedeemCode.LinkedTypeEnum.Collection}
					title={codeTitle}
					description={codeText}
					backgroundImage={codeBackgroundMediaItem}
					webPrice={hasWebPrice && collection.pricing?.web_price}
					footer={<BundleUnlockButton bundles={unlockingBundles} accountId={collection.account_id} />}
				/>
			)
		} else if (ownLock && lockCollection.loading) {
			return <DefaultPagePlaceholder />
		}

		const headerProps: HeaderProps = {
			loading,
			type: this.showMapLayout ? 'short' : (headerLayout as 'short' | 'tall' | 'image'),
			title,
			mappable: mappableList && !this.showMapLayout,
			onMapToggle: () => this.toggleMapModal(true),
			onBackButton: onBackAction,
			hideBackButton: isInsideModal,
			showBackButton: Boolean(onBackAction),
			coverImage: coverImageUrl
				? {
						imageData: coverImage,
						imageUrl: coverImageUrl,
						imageSrcSet: coverImageSrcSet,
					}
				: null,
			logoImage: logoImageUrl
				? {
						imageData: logo,
						imageUrl: logoImageUrl,
						imageSrcSet: logoImageSrcSet,
					}
				: null,
			logoPlacement,
			pageType: this.collectionType,

			collection: collection,
			screen: null,
			collectionItem: null,
			hideFavouriteButton: false,
			itemId: collection.id,
			itemType: GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection,
		}

		if (!loading && (!collection || error?.status === 404)) {
			return <Page404 />
		}

		return (
			<>
				<Helmet
					title={title}
					meta={[
						{ name: 'description', content: description || title },
						{ property: 'og:type', content: 'website' }, // TODO: check type
						{ property: 'og:title', content: title },
						{ property: 'og:description', content: description || title },
						{ property: 'og:image', content: coverImageUrl },
						{ property: 'og:url', content: window.location.href },
					]}
				/>
				{isPageContent ? (
					<>
						<ErrorMessageHandler error={error} />
						{!error && (
							<CollectionListContent
								collection={collection}
								loading={loading}
								items={this.listItems}
								onItemClicked={this.onItemClicked}
							/>
						)}
					</>
				) : (
					<Page
						headerProps={!this.showMapLayout && headerProps}
						contentProps={{
							className: this.showMapLayout ? 'map-layout' : '',
						}}
						footer={{
							component: !isInsideModal && (
								<AudioPlayerBarComponent
									bottomOffset={showMap || this.showMapLayout ? 0 : 56}
									className={classNames({ 'full-width': this.showMapLayout })}
								/>
							),
						}}
						dataItemId={String(collection.id)}
						dataItemType="collection"
						dataItemSubtype="list"
						dataCy="page-collection-list"
					>
						<ErrorMessageHandler error={error} />
						{!error &&
							(this.showMapLayout ? (
								this.renderMapContent({ showMapLayout: true })
							) : (
								<CollectionListContent
									collection={collection}
									loading={loading}
									items={this.listItems}
									onItemClicked={this.onItemClicked}
								/>
							))}
						{!error && showMap && !this.showMapLayout && this.renderMap()}
					</Page>
				)}
			</>
		)
	}
}

const CollectionListComponent = Container.default(CollectionList)
export default CollectionListComponent as any
